<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="hv-header">
        <span class="hv-bar hv-bar--xl"></span>
        <span class="hv-bar hv-bar--md"></span>
      </div>
      <div class="hv-canvas">
        <div class="hv-shape hv-shape--a"></div>
        <div class="hv-shape hv-shape--b"></div>
        <span
          v-for="(zone, i) in zones"
          :key="i"
          class="hv-hot"
          :class="`--size-${zone.size}`"
          :style="{ left: zone.x + '%', top: zone.y + '%', '--i': i }"
        ></span>
        <span class="hv-pulse"></span>
      </div>
      <div class="hv-caption">
        <span class="hv-bar hv-bar--md hv-bar--accent"></span>
      </div>
    </template>

    <template #audience>
      <span class="hv-phone-q"></span>
      <div class="hv-phone-hint">
        Tap an area:
      </div>
      <div class="hv-phone-canvas">
        <div class="hv-shape hv-shape--a hv-shape--mini"></div>
        <div class="hv-shape hv-shape--b hv-shape--mini"></div>
        <span class="hv-phone-tap"></span>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewHeatmapVote',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      zones: [
        { x: 28, y: 35, size: 'lg' },
        { x: 35, y: 50, size: 'md' },
        { x: 24, y: 55, size: 'sm' },
        { x: 70, y: 40, size: 'md' },
        { x: 65, y: 60, size: 'sm' },
        { x: 50, y: 70, size: 'sm' },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.hv-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.hv-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.hv-bar--md { width: 40%; }
.hv-bar--accent { background: var(--accent); }

.hv-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.hv-canvas {
  position: relative;
  height: 130px;
  border-radius: 10px;
  background: rgba(15, 15, 20, 0.04);
  border: 1px solid rgba(15, 15, 20, 0.08);
  overflow: hidden;
}
.hv-shape {
  position: absolute;
  background: rgba(15, 15, 20, 0.12);
}
.hv-shape--a {
  width: 28%; height: 60%;
  left: 18%; top: 25%;
  border-radius: 8px;
}
.hv-shape--b {
  width: 24%; height: 50%;
  left: 60%; top: 30%;
  border-radius: 999px;
}
.hv-hot {
  position: absolute;
  border-radius: 999px;
  background: var(--accent);
  transform: translate(-50%, -50%);
  animation: hv-pop 4s ease-out infinite;
  animation-delay: calc(var(--i) * 200ms);
  opacity: 0;
}
.hv-hot.--size-sm { width: 12px; height: 12px; opacity: 0.5; }
.hv-hot.--size-md { width: 18px; height: 18px; opacity: 0.7; }
.hv-hot.--size-lg {
  width: 26px; height: 26px;
  box-shadow: 0 0 12px color-mix(in srgb, var(--accent) 60%, transparent);
}
.hv-pulse {
  position: absolute;
  width: 14px; height: 14px;
  border-radius: 999px;
  background: var(--accent);
  left: 28%; top: 35%;
  transform: translate(-50%, -50%);
  animation: hv-pulse 4s ease-out infinite;
}

.hv-caption {
  display: flex; justify-content: center;
  margin-top: 2px;
}

/* phone */
.hv-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.hv-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.hv-phone-canvas {
  position: relative;
  flex: 1;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.04);
  border: 1px solid rgba(15, 15, 20, 0.08);
  min-height: 120px;
  overflow: hidden;
}
.hv-shape--mini.hv-shape--a {
  width: 30%; height: 60%;
  left: 14%; top: 20%;
}
.hv-shape--mini.hv-shape--b {
  width: 24%; height: 48%;
  left: 60%; top: 30%;
}
.hv-phone-tap {
  position: absolute;
  width: 20px; height: 20px;
  border-radius: 999px;
  background: var(--accent);
  left: 30%; top: 45%;
  transform: translate(-50%, -50%);
  animation: hv-tap 4s ease-out infinite;
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent);
}

@keyframes hv-pop {
  0%, 10%   { opacity: 0; transform: translate(-50%, -50%) scale(0); }
  25%       { transform: translate(-50%, -50%) scale(1.3); }
  35%, 90%  { transform: translate(-50%, -50%) scale(1); }
  100%      { transform: translate(-50%, -50%) scale(1); }
}
@keyframes hv-pulse {
  0%, 30%   { transform: translate(-50%, -50%) scale(1); opacity: 0.9; }
  60%       { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
  100%      { transform: translate(-50%, -50%) scale(1); opacity: 0.9; }
}
@keyframes hv-tap {
  0%, 25%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent); }
  60%       { box-shadow: 0 0 0 14px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .hv-hot, .hv-pulse, .hv-phone-tap { animation: none; opacity: 1; }
  .hv-hot { transform: translate(-50%, -50%) scale(1); }
}
</style>
